import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Compass, Calendar, ArrowLeft, Home, Ticket, HelpCircle } from "lucide-react";

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 sm:py-24">
        <div className="max-w-2xl w-full text-center space-y-8">
          {/* Visual Element */}
          <div className="relative inline-flex items-center justify-center">
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-amber-100/60 dark:bg-amber-950/40 flex items-center justify-center border border-amber-200/80 dark:border-amber-800/40 shadow-inner">
              <Compass size={54} className="text-[color:var(--brand-terracotta)] animate-[spin_18s_linear_infinite]" />
            </div>
            <div className="absolute -bottom-2 px-3 py-1 rounded-full bg-background border border-border shadow-sm text-xs font-mono font-semibold tracking-wider text-stone-600 dark:text-stone-300">
              ERROR 404
            </div>
          </div>

          {/* Heading & Copy */}
          <div className="space-y-3">
            <div className="eyebrow text-xs tracking-widest text-[color:var(--brand-terracotta)]">
              LOST ALONG THE COAST?
            </div>
            <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-foreground">
              This page could not be found
            </h1>
            <p className="text-stone-600 dark:text-stone-400 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
              The link you followed may have expired, moved, or never existed. Let's get you back on common ground.
            </p>
          </div>

          {/* Navigation CTA Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <Button
              onClick={() => navigate(-1)}
              variant="outline"
              className="h-11 px-5 border-border"
            >
              <ArrowLeft size={16} className="mr-2" /> Go back
            </Button>

            <Link to="/events">
              <Button className="btn-brand h-11 px-6 shadow-sm">
                <Calendar size={16} className="mr-2" /> Explore Events
              </Button>
            </Link>

            <Link to="/">
              <Button variant="secondary" className="h-11 px-5">
                <Home size={16} className="mr-2" /> Home
              </Button>
            </Link>
          </div>

          {/* Helpful Quick Links Card */}
          <div className="pt-8">
            <div className="cgc-card p-6 max-w-md mx-auto text-left border border-border/80 bg-card/60 backdrop-blur-sm">
              <div className="text-xs font-mono uppercase tracking-wider text-stone-500 mb-3">
                Looking for something specific?
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <Link
                  to="/portal"
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary transition-colors text-stone-700 dark:text-stone-300"
                >
                  <Ticket size={15} className="text-[color:var(--brand-terracotta)]" />
                  <span>My Ticket Portal</span>
                </Link>
                <Link
                  to="/impact"
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary transition-colors text-stone-700 dark:text-stone-300"
                >
                  <Compass size={15} className="text-[color:var(--brand-terracotta)]" />
                  <span>Impact Metrics</span>
                </Link>
                <Link
                  to="/help"
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary transition-colors text-stone-700 dark:text-stone-300"
                >
                  <HelpCircle size={15} className="text-[color:var(--brand-terracotta)]" />
                  <span>Help & FAQs</span>
                </Link>
                <Link
                  to="/events"
                  className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary transition-colors text-stone-700 dark:text-stone-300"
                >
                  <Calendar size={15} className="text-[color:var(--brand-terracotta)]" />
                  <span>All Events</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
